import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { gbp, fmtDateTime } from "@/lib/format";
import { Notice } from "@/components/Badges";
import { IssueList } from "@/components/application/ChecksPanel";

const FEE_KIND = { patent: "patent", trade_mark: "tm", design: "design" };

const Check = ({ id, checked, onChange, children }) => (
  <label className="flex items-start gap-3 text-sm"><input type="checkbox" data-testid={id} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[#0D9488]" />{children}</label>
);

const ExpertReview = ({ app }) => {
  const [note, setNote] = useState("");
  const send = async () => {
    await api.post(`/applications/${app.id}/expert-review`, { note });
    setNote("");
    toast.success("Confidential review request sent. An adviser will quote before any work starts.");
  };
  return (
    <section className="card p-6" data-testid="expert-review-section">
      <h2 className="font-semibold text-[#0B192C]">Request confidential expert review</h2>
      <textarea aria-label="Note for reviewer" rows={3} data-testid="expert-review-note" className="input mt-3" placeholder="What would you like reviewed?" value={note} onChange={(e) => setNote(e.target.value)} />
      <button onClick={send} data-testid="expert-review-submit" className="btn-ghost btn-sm mt-2">Send request</button>
    </section>
  );
};

export default function ReviewApprove({ app, reload }) {
  const [fees, setFees] = useState([]);
  const [decl, setDecl] = useState([]);
  const [f, setF] = useState({ authority_confirmed: false, disclosures_ack: false, fees_ack: false, signer_name: "", signer_role: "" });
  useEffect(() => {
    api.get("/public/pricing").then(({ data }) => setFees(data.filter((p) => p.category === "official" && FEE_KIND[app.kind] && p.id.includes(FEE_KIND[app.kind]))));
  }, [app.kind]);
  const markReady = async () => {
    try { await api.put(`/applications/${app.id}/status`, { status: "awaiting_review" }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async () => {
    try {
      await api.post(`/applications/${app.id}/approve`, { ...f, declarations: decl });
      toast.success("Exact version approved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const docs = app.documents.length;
  const s = app.checks.budget;
  return (
    <div className="space-y-6" data-testid="final-review">
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card p-6"><h2 className="mb-3 font-semibold text-[#0B192C]">Evidence and completeness</h2>
          <ul className="mb-4 space-y-1 text-sm">{app.sections.map((x) => <li key={x.key} className="flex justify-between"><span>{x.title}</span><span className="caption">{x.evidence.length} evidence links · {x.provenance.replace("_", " ")}</span></li>)}</ul>
          <IssueList checks={app.checks} />
        </section>
        <section className="card p-6"><h2 className="font-semibold text-[#0B192C]">Costs and fees</h2>
          {s && <p className="mt-2 text-sm">Total costs {gbp(s.total_costs)} · Grant requested <b>{gbp(s.grant_requested)}</b> · Your contribution {gbp(s.business_contribution)}</p>}
          {fees.length > 0 && <ul className="mt-3 space-y-1 text-sm">{fees.map((p) => <li key={p.id}>{p.name}: <b>{gbp(p.amount)}</b> <span className="text-xs text-slate-500">(official fee, paid to the official body)</span></li>)}</ul>}
          <p className="mt-3 text-xs text-slate-500">Platform charges and any professional fees are shown separately on the Pricing page. No fee is taken by this approval.</p>
          <h3 className="mt-5 text-sm font-semibold">Exact documents being submitted ({docs})</h3>
          <p className="text-xs text-slate-500">{docs ? "Listed on the Documents & checks tab and included in the export pack." : "No documents selected."}</p>
          {app.approval && <p className="mt-4 text-xs" data-testid="approval-record">Approved v{app.approval.version_number} by {app.approval.signer_name} ({app.approval.signer_role}) · {fmtDateTime(app.approval.at)} · {app.approval_valid ? "valid for current content" : "INVALIDATED by later edits"}</p>}
        </section>
      </div>
      {app.kind === "grant" && app.status === "draft" && (
        <button onClick={markReady} disabled={!app.checks.ready} data-testid="mark-ready-button" className="btn-teal">Move to awaiting review</button>
      )}
      {((app.kind === "grant" && app.status === "awaiting_review") || (app.kind !== "grant" && app.status === "draft")) && (
        <section className="card space-y-3 p-6" data-testid="approval-form">
          <h2 className="font-semibold text-[#0B192C]">Declarations and approval</h2>
          {app._role !== "owner" && <Notice tone="warn">Only a workspace owner can approve.</Notice>}
          {app.template.declarations.map((d, i) => <Check key={d} id={`declaration-${i}`} checked={decl.includes(d)} onChange={(c) => setDecl(c ? [...decl, d] : decl.filter((x) => x !== d))}>{d}</Check>)}
          <Check id="authority-checkbox" checked={f.authority_confirmed} onChange={(c) => setF({ ...f, authority_confirmed: c })}>I have authority to act for the applicant.</Check>
          <Check id="disclosures-checkbox" checked={f.disclosures_ack} onChange={(c) => setF({ ...f, disclosures_ack: c })}>I understand which information will be disclosed externally (including any public summary).</Check>
          <Check id="fees-checkbox" checked={f.fees_ack} onChange={(c) => setF({ ...f, fees_ack: c })}>I understand the fees that apply and who they are paid to.</Check>
          <div className="grid gap-3 sm:grid-cols-2">
            <input aria-label="Your full name" data-testid="signer-name-input" className="input" placeholder="Full name" value={f.signer_name} onChange={(e) => setF({ ...f, signer_name: e.target.value })} />
            <input aria-label="Your role" data-testid="signer-role-input" className="input" placeholder="Role, e.g. Director" value={f.signer_role} onChange={(e) => setF({ ...f, signer_role: e.target.value })} />
          </div>
          <button onClick={approve} data-testid="approve-button" className="btn-teal">Approve this exact version</button>
        </section>
      )}
      <ExpertReview app={app} />
    </div>
  );
}
